import type { AuthProvider, UserIdentity } from "react-admin";
import { authClient } from "@/auth";

export type UserRole = "user" | "admin" | "super_admin";

export interface AdminUser extends UserIdentity {
	role: UserRole;
	email: string;
}

interface LoginParams {
	username: string;
	password: string;
	captchaHeaders?: Record<string, string>;
}

export const authProvider: AuthProvider = {
	login: async ({ username, password, captchaHeaders = {} }: LoginParams) => {
		const result = await authClient.signIn.email(
			{
				email: username,
				password,
			},
			{
				headers: captchaHeaders,
			},
		);

		if (result.error) {
			throw new Error(result.error.message || "Login failed");
		}

		// Verify user has admin role
		const session = await authClient.getSession();
		const user = session.data?.user as { role?: UserRole } | undefined;

		if (!user?.role || (user.role !== "admin" && user.role !== "super_admin")) {
			await authClient.signOut();
			throw new Error("Access denied. Admin privileges required.");
		}

		return Promise.resolve();
	},

	logout: async () => {
		await authClient.signOut();
		return Promise.resolve();
	},

	checkAuth: async () => {
		const session = await authClient.getSession();

		if (!session.data?.user) {
			throw new Error("Not authenticated");
		}

		const user = session.data.user as { role?: UserRole };

		if (!user.role || (user.role !== "admin" && user.role !== "super_admin")) {
			throw new Error("Access denied. Admin privileges required.");
		}

		return Promise.resolve();
	},

	checkError: async (error: { status?: number }) => {
		if (error.status === 401 || error.status === 403) {
			throw new Error("Session expired");
		}
		return Promise.resolve();
	},

	getIdentity: async (): Promise<AdminUser> => {
		const session = await authClient.getSession();
		const user = session.data?.user as
			| {
					id: string;
					name?: string;
					email: string;
					image?: string;
					role: UserRole;
			  }
			| undefined;

		if (!user) {
			throw new Error("Not authenticated");
		}

		return {
			id: user.id,
			fullName: user.name || user.email,
			avatar: user.image || undefined,
			email: user.email,
			role: user.role,
		};
	},

	getPermissions: async (): Promise<{ role: UserRole }> => {
		const session = await authClient.getSession();
		const user = session.data?.user as { role?: UserRole } | undefined;

		return { role: user?.role || "user" };
	},
};
